import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from './firebaseConfig';
import { 
  collection, 
  getDocs, 
  addDoc, 
  query, 
  where, 
  getDoc, 
  doc, 
  deleteDoc, 
  updateDoc, 
  arrayUnion,
  Timestamp
} from 'firebase/firestore';

const projectData = {
  getProjectsByUserId: async (userId) => {
    console.log('[DEBUG] Iniciando búsqueda en BD para userId:', userId);
    
    // Crear una query que busque proyectos donde el usuario tenga acceso
    const queries = [
      // Buscar por userId tradicional
      query(collection(db, 'projects'), where('userId', '==', userId)),
      // Buscar por ownerId
      query(collection(db, 'projects'), where('ownerId', '==', userId)),
      // Buscar por permisos
      query(collection(db, 'projects'), where(`permissions.${userId}`, 'in', ['owner', 'write', 'read']))
    ];

    try {
      const results = await Promise.all(queries.map(q => getDocs(q)));
      
      // Combinar resultados y eliminar duplicados por ID
      const projectsMap = new Map();
      
      results.forEach(querySnapshot => {
        querySnapshot.docs.forEach(doc => {
          const projectData = { id: doc.id, ...doc.data() };
          projectsMap.set(doc.id, projectData);
        });
      });

      const projects = Array.from(projectsMap.values());
      
      console.log('[DEBUG] Proyectos encontrados:', {
        totalProjects: projects.length,
        projectIds: projects.map(p => p.id)
      });

      return projects;
    } catch (error) {
      console.error('[ERROR] Error en getProjectsByUserId:', error);
      throw error;
    }
  },
  createProject: async (projectData) => {
    const NOW = Timestamp.now()
    const docRef = await addDoc(collection(db, 'projects'), {
      ...projectData,
      createdAt: NOW, // timestamp de creación
      updatedAt: NOW, // timestamp de actualización
    });
    const docSnap = await getDoc(docRef);
    return { id: docSnap.id, ...docSnap.data() };
  },
  deleteProject: async (projectId) => {
    const projectRef = doc(db, 'projects', projectId);
    const projectSnapshot = await getDoc(projectRef);
  
    if (projectSnapshot.exists()) {
      const projectData = projectSnapshot.data();
  
      // Solo intenta eliminar la imagen del Storage si no es la imagen por defecto
      if (projectData.imageUrl && !projectData.imageUrl.includes('no-image')) {
        const imageRef = ref(storage, projectData.imageUrl);
        try {
          await deleteObject(imageRef);
        } catch (error) {
          console.error('Error deleting image from storage:', error);
        }
      }
  
      // Elimina el documento del proyecto en Firestore
      await deleteDoc(projectRef);
    } 
  },
  updateProject: async (projectId, updateData) => {
    const projectRef = doc(db, 'projects', projectId);
    await updateDoc(projectRef, {
      ...updateData, 
      updatedAt: Timestamp.now()
    });
    const updatedDoc = await getDoc(projectRef);
    return { id: updatedDoc.id, ...updatedDoc.data() };
  },
  getProjectById: async (userId, projectId) => {
    console.log('[DEBUG] Buscando proyecto específico:', {
      userId,
      projectId
    });

    if (!projectId) {
      console.warn('[WARN] ProjectId es nulo o indefinido');
      throw new Error('Project ID is missing');
    }
    
    const projectRef = doc(db, 'projects', projectId);
    const projectSnap = await getDoc(projectRef);
    
    console.log('[DEBUG] Resultado de búsqueda:', {
      exists: projectSnap.exists(),
      data: projectSnap.exists() ? projectSnap.data() : null
    });

    if (projectSnap.exists()) {
      const projectData = projectSnap.data();
      return { id: projectSnap.id, ...projectData };
    } else {
      console.warn('[WARN] Proyecto no encontrado en BD');
      return null;
    }
  },
  
  addComponentToProject: async (projectId, component) => {
    const projectRef = doc(db, 'projects', projectId);
    await updateDoc(projectRef, {
      components: arrayUnion(component)
    });
  },
  removeComponentFromProject: async (projectId, idLocal) => {
    const projectRef = doc(db, 'projects', projectId);
    const projectSnap = await getDoc(projectRef);

    if (projectSnap.exists()) {
        const projectData = projectSnap.data();
        const components = projectData.components || []; // Asegurarse de que sea un array
        const updatedComponents = components.filter(component => component.id !== idLocal);

        await updateDoc(projectRef, {
            components: updatedComponents
        });

        return { success: true, id: idLocal }; // Confirmación de eliminación
    } else {
        console.error('Project not found');
        throw new Error('Project not found');
    }
},
  uploadImageToProject: async (file) => {
    const allowedTypes = ['image/png', 'image/jpeg'];
    // Verifica si el archivo es de un tipo permitido
    if (!allowedTypes.includes(file.type)) {
      throw new Error('El archivo debe ser una imagen en formato png o jpg');
    }
    const timestamp = Date.now();
    const sanitizedFileName = `${timestamp}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
    const storageRef = ref(storage, `project/${sanitizedFileName}`);
  
    // Subir el archivo
    await uploadBytes(storageRef, file);
  
    const url = await getDownloadURL(storageRef);
    return url;
  },
  getNextAvailableComponentId: async (projectId, prefix) => {
    const projectRef = doc(db, 'projects', projectId);
    const projectSnapshot = await getDoc(projectRef);

    if (!projectSnapshot.exists()) {
      throw new Error('El proyecto no existe');
    }

    const projectData = projectSnapshot.data();
    const components = projectData.components || [];

    // Obtener todos los ids locales de los componentes con el prefijo
    const ids = components
    .filter(component => component.id && component.id.startsWith(prefix)) // Verifica que el id existe y empieza con el prefijo
    .map(component => parseInt(component.id.replace(prefix, ''))) // Obtener el número
    .sort((a, b) => a - b); // Ordenar por número ascendente

    // Buscar el primer ID disponible en la secuencia
    let nextId = 1; // Empezar en 1
    for (const id of ids) {
      if (id === nextId) {
        nextId++; // Si el ID actual está ocupado, seguir buscando
      } else {
        break; // Si encontramos un hueco, dejar de incrementar
      }
    }

    return `${prefix}${nextId}`; // Retornar el primer ID disponible
  },
  removeConnectionsFromProject: async (projectId, connectionsToRemove) => {
    const projectRef = doc(db, 'projects', projectId);
    const projectSnap = await getDoc(projectRef);
  
    if (projectSnap.exists()) {
      const projectData = projectSnap.data();
      const updatedConnections = projectData.connections.filter(connection => 
        !connectionsToRemove.some(toRemove => toRemove.id === connection.id)
      );
  
      await updateDoc(projectRef, {
        connections: updatedConnections
      });
    } else {
      throw new Error('Project not found');
    }
  },
  async saveConnectionToDB(projectId, connection) {
    const projectRef = doc(db, 'projects', projectId);
    const projectSnap = await getDoc(projectRef);

    if (!projectSnap.exists()) {
        throw new Error('Project not found');
    }

    const projectData = projectSnap.data();
    const updatedConnections = [...(projectData.connections || []), connection];

    await updateDoc(projectRef, { connections: updatedConnections });
  },
  getProjectsWithUserAccess: async (userId) => {
    const q = query(
      collection(db, 'projects'),
      where(`permissions.${userId}`, 'in', ['owner', 'write', 'read'])
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },
  addProjectPermission: async (projectId, userEmail, permissionLevel) => {
    // Primero obtener el userId del email
    const userQuery = query(collection(db, 'users'), where('email', '==', userEmail));
    const userSnapshot = await getDocs(userQuery);
    
    if (userSnapshot.empty) {
      throw new Error('Usuario no encontrado');
    }

    const userId = userSnapshot.docs[0].id;
    const projectRef = doc(db, 'projects', projectId);
    
    await updateDoc(projectRef, {
      [`permissions.${userId}`]: permissionLevel
    });
  }
};

export default projectData;